/**
 * Ouverture de documents et copie de texte fiables, y compris lorsque
 * l'application tourne dans une iframe (aperçu intégré) où `window.open`
 * et l'API Clipboard sont souvent bloqués par le navigateur.
 */

/** Document parent réellement accessible (sinon document courant). */
const safeDoc = (): Document => {
  try {
    const top = window.top;
    if (top && top !== window) {
      const d = top.document;
      void d.body.childElementCount; // lève si cross-origin
      return d;
    }
  } catch {
    /* repli ci-dessous */
  }
  return document;
};

/** Ouvre une URL dans un nouvel onglet via un lien réel (contourne les bloqueurs de pop-up). */
export function openUrlInNewTab(url: string): boolean {
  try {
    const doc = safeDoc();
    const a = doc.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    doc.body.appendChild(a);
    a.click();
    setTimeout(() => a.remove(), 0);
    return true;
  } catch {
    try {
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      return !!w;
    } catch {
      return false;
    }
  }
}

/**
 * Affiche un document HTML généré dans une visionneuse intégrée à l'application
 * (aucun nouvel onglet, aucune URL technique exposée).
 */
export function openHtmlPreview(html: string, title = 'Aperçu du document'): boolean {
  try {
    const host = document.createElement('div');
    host.setAttribute('role', 'dialog');
    host.setAttribute('aria-label', title);
    host.style.cssText =
      'position:fixed;inset:0;z-index:99999;background:rgba(2,6,23,.88);display:flex;flex-direction:column;';

    const bar = document.createElement('div');
    bar.style.cssText =
      'display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 16px;background:#0f172a;border-bottom:1px solid #1e293b;color:#e2e8f0;font:500 14px/1.2 system-ui,sans-serif;';

    const label = document.createElement('span');
    label.textContent = title;

    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;gap:8px;';

    const btnStyle =
      'padding:6px 14px;border-radius:8px;border:1px solid #334155;background:#1e293b;color:#e2e8f0;font:500 13px system-ui,sans-serif;cursor:pointer;';
    const printBtn = document.createElement('button');
    printBtn.type = 'button';
    printBtn.textContent = 'Imprimer';
    printBtn.style.cssText = btnStyle;
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.textContent = 'Fermer';
    closeBtn.style.cssText = btnStyle;

    actions.append(printBtn, closeBtn);
    bar.append(label, actions);

    const frame = document.createElement('iframe');
    frame.title = title;
    frame.style.cssText = 'flex:1;width:100%;border:none;background:#fff;';
    frame.srcdoc = html;

    const close = () => {
      host.remove();
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    closeBtn.addEventListener('click', close);
    printBtn.addEventListener('click', () => {
      try {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      } catch {
        /* impression indisponible */
      }
    });
    document.addEventListener('keydown', onKey);

    host.append(bar, frame);
    document.body.appendChild(host);
    return true;
  } catch {
    return false;
  }
}

/** Compatibilité : l'aperçu s'ouvre désormais dans la visionneuse intégrée. */
export function openHtmlInNewTab(html: string): boolean {
  return openHtmlPreview(html);
}

/** Déclenche le téléchargement d'un blob via un lien réel. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const doc = safeDoc();
    const a = doc.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    a.style.display = 'none';
    doc.body.appendChild(a);
    a.click();
    setTimeout(() => a.remove(), 0);
  } catch {
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => a.remove(), 0);
    } catch {
      /* dernier repli : ouverture directe */
      window.open(url, '_blank');
    }
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 120000);
  }
}


/** Copie du texte avec repli `execCommand` si l'API Clipboard est indisponible. */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* repli ci-dessous */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}
